import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

export const fetchCountry = createAsyncThunk(
  "member/fetchCountry",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-countries`); 
      const data = response.data.map(row => ({ value: row.id, label: row.name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load country");
    }
  }
);
export const fetchCity = createAsyncThunk(
  "member/fetchCity",
  async (country_id, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-cities?country_id=`+country_id); 
      const data = response.data.map(row => ({ value: row.id, label: row.name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load city");
    }
  }
);

export const fetchOffice = createAsyncThunk(
  "member/fetchOffice",
  async (city_id, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/fetch-offices?city_id=`+city_id); 
      const data = response.data.map(row => ({ value: row.id, label: row.office_name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load office");
    }
  }
);

export const addMember = createAsyncThunk(
  "member/addMember",
  async (data, { rejectWithValue }) => {
    try {
      let formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if ((key === 'profile_image' || key === 'id_proof') && !(value instanceof File)) {
          formData.append(key, '');
        } else {
          formData.append(key, value);
        }
      });

      let url = "";
      if (data.id) {
        url += `/update/${data.id}`;
      }

      const response = await api.post("admin/members" + url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to add user");
    }
  }
);

export const getMember = createAsyncThunk(
  "member/getMember",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/members?type=${data.type}&page=${data.page}&perPage=${data.perPage}&search=${data.search}&filter=${data.filter}&city_id=${data.city}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to get user");
    }
  }
);

export const deleteMember = createAsyncThunk(
  "member/deleteMember",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.del("admin/users/" + id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to delete user");
    }
  }
);

export const approveMember = createAsyncThunk(
  "member/approveMember",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/approve-customer/" + data.id, { status: data.value });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load user");
    }
  }
);

const memberSlice = createSlice({
  name: "member",
  initialState: {
    list_users: [],
    selected: null,
    countries: [],
    cities: [],
    offices:[],
    status: "idle",
    loading: false,
    list_loading:false
  },
  reducers: {
    setSelectedMember(state, action) {
      const payload = action.payload || {};
      state.selected = {
        id                    :payload.id,
        first_name            :payload.first_name,  
        email                 :payload.email,
        parent_id             :payload.parent_id,
        mobile_number         :payload.mobile_number,
        alternate_number      :payload.alternate_number || null, 
        alternate_country_code:payload.alternate_country_code || null,
        country_code          :payload.country_code || null,
        designation           :payload.designation,
        profile_image         :payload.profile_image,
        residence_id          :payload.residence_id,
        id_proof              :payload.residence_image,
        role_id               :payload.roles[0]?.id,
        country_id            :payload.office.country_id,
        city_id               :payload.office.city_id,
        office_id             :payload.office.id,
        status                :payload.status,
        type                  :payload.roles[0]?.name,
        permissions           :payload.permissions
      };
    },
    resetStatus(state) {
      state.status  = "idle";
      state.error   = null;
      state.loading = false;
      state.list_loading = false
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(fetchCountry.fulfilled, (state, action) => {
        state.countries   = action.payload;
    })
    .addCase(fetchCity.fulfilled, (state, action) => {
        state.cities   = action.payload;
    })
    .addCase(fetchOffice.fulfilled, (state, action) => {
      state.offices   = action.payload;
    })
    .addCase(addMember.pending, (state) => {
      state.status = "loading";
      state.loading = true;
    })
    .addCase(addMember.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "idle";
    })
    .addCase(addMember.rejected, (state, action) => {
      state.status  = "error";
      state.loading = false;
    })
    .addCase(getMember.pending, (state) => {
      state.status = "loading";
      state.list_loading = true;
    })
    .addCase(getMember.fulfilled, (state, action) => {
      state.list_loading = false;
      state.status = "idle";
      state.list_users = action.payload;
    })
    .addCase(getMember.rejected, (state, action) => {
      state.status  = "error";
      state.list_loading = false;
    })
    .addCase(deleteMember.pending, (state) => {
      state.status = "loading";
      state.loading = true;
    })
    .addCase(deleteMember.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "idle";
    })
    .addCase(deleteMember.rejected, (state, action) => {
      state.status  = "error";
      state.loading = false;
    })
    .addCase(approveMember.pending, (state) => {
      state.status = "loading";
      state.loading = true;
    })
    .addCase(approveMember.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "idle";
    })
    .addCase(approveMember.rejected, (state, action) => {
      state.status  = "error";
      state.loading = false;
    })
  }
});

export const { setSelectedMember, resetStatus } = memberSlice.actions;
export default memberSlice.reducer;
